import { Modal, Descriptions, Tag, Button, Typography, Space, Card, List, message } from "antd"
import { CopyOutlined, FireOutlined, ClockCircleOutlined, SoundOutlined } from "@ant-design/icons"
import type React from "react"
import type { AiWordItem } from "@/services/log/aiWordApi"
import { WEB_URL } from "@/configs/config"
import dayjs from "dayjs"

const { Text, Paragraph, Title } = Typography

interface AiWordDetailModalProps {
  open: boolean
  record: AiWordItem | null
  onClose: () => void
}

const getPosColor = (pos?: string) => {
  if (!pos) return "default"
  const normalized = pos.trim().toLowerCase()
  if (normalized.startsWith("noun")) return "blue"
  if (normalized.startsWith("verb")) return "gold"
  if (normalized.startsWith("adj")) return "green"
  if (normalized.startsWith("adv")) return "purple"
  return "cyan"
}

export const AiWordDetailModal: React.FC<AiWordDetailModalProps> = ({ open, record, onClose }) => {
  if (!record) return null

  const { data } = record
  const examples = data?.examples || []

  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(record, null, 2))
    message.success("Đã sao chép toàn bộ dữ liệu JSON!")
  }

  const handleSpeak = (text: string) => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = "en-US"
      window.speechSynthesis.speak(utterance)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center justify-between pr-8">
          <Space align="center" size="middle">
            <span className="font-bold text-lg">Chi tiết Từ vựng AI</span>
            {record.word && (
              <Button
                type="text"
                size="small"
                icon={<SoundOutlined className="text-blue-500" />}
                onClick={() => handleSpeak(record.word)}
              >
                Phát âm
              </Button>
            )}
          </Space>
          <Button size="small" icon={<CopyOutlined />} onClick={handleCopyJson}>
            Copy JSON
          </Button>
        </div>
      }
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      width={720}
      centered
      destroyOnClose
    >
      <div className="flex flex-col gap-4 py-2">
        {/* Header summary banner */}
        <div className="flex flex-wrap items-center justify-between rounded-lg border border-gray-100 bg-gray-50 p-4">
          <div>
            <div className="flex items-center gap-2">
              <a
                href={`${WEB_URL}/en-vi/dictionary/${record.slug || encodeURIComponent(record.word)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80"
              >
                <Title level={3} className="m-0! text-blue-600 hover:underline">
                  {record.word}
                </Title>
              </a>
              {data?.part_of_speech && (
                <Tag color={getPosColor(data.part_of_speech)} className="font-semibold text-sm">
                  {data.part_of_speech}
                </Tag>
              )}
            </div>
            {data?.ipa && (
              <Text type="secondary" className="font-mono text-base">
                {data.ipa}
              </Text>
            )}
          </div>

          <div className="flex flex-col items-end gap-1.5">
            <Space>
              <Text strong>Lượt tìm kiếm:</Text>
              <Tag
                color={record.search_count >= 10 ? "error" : "blue"}
                icon={record.search_count >= 10 ? <FireOutlined /> : undefined}
                className="font-bold text-sm"
              >
                {record.search_count}
              </Tag>
            </Space>
            <Space>
              <Text strong>Lượt thêm:</Text>
              <Tag color={(record.add_count ?? 0) >= 5 ? "green" : "default"} className="font-bold text-sm">
                {record.add_count ?? 0}
              </Tag>
            </Space>
          </div>
        </div>

        {/* Nghĩa & Định nghĩa */}
        <Card size="small" title="Định nghĩa & Ý nghĩa" className="shadow-xs">
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Nghĩa tiếng Việt">
              <Text strong className="text-base text-gray-900">
                {data?.meaning || "—"}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Định nghĩa tiếng Anh">
              <Paragraph className="m-0 text-gray-700 italic">{data?.definition || "—"}</Paragraph>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Danh sách câu ví dụ song ngữ */}
        <Card size="small" title={`Ví dụ song ngữ (${examples.length})`} className="shadow-xs">
          {examples.length === 0 ? (
            <Text type="secondary" italic>
              Không có câu ví dụ nào.
            </Text>
          ) : (
            <List
              dataSource={examples}
              renderItem={(item, index) => (
                <List.Item className="px-2! py-2!">
                  <div className="flex w-full flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <Tag color="cyan" className="m-0 mt-0.5 text-xs">
                        #{index + 1}
                      </Tag>
                      <Text strong className="text-gray-900 text-sm">
                        {item.sentence}
                      </Text>
                      <SoundOutlined
                        className="mt-1 cursor-pointer text-blue-500 hover:text-blue-600"
                        onClick={() => handleSpeak(item.sentence)}
                      />
                    </div>
                    <div className="pl-7 text-gray-600 text-xs italic">{item.meaning}</div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Card>

        {/* Meta info */}
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Slug">
            {record.slug ? (
              <a
                href={`${WEB_URL}/en-vi/dictionary/${record.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-blue-600 hover:underline"
              >
                {record.slug}
              </a>
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="ID hệ thống">#{record.id}</Descriptions.Item>
          <Descriptions.Item label="Thời gian tạo">
            <Space size={4}>
              <ClockCircleOutlined />
              <span>{record.created_at ? dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss") : "—"}</span>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Cập nhật lần cuối">
            <Space size={4}>
              <ClockCircleOutlined />
              <span>{record.updated_at ? dayjs(record.updated_at).format("DD/MM/YYYY HH:mm:ss") : "—"}</span>
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Modal>
  )
}

export default AiWordDetailModal
